import React, { useState, useMemo, useEffect, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, Filter, Grid, List, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input.tsx";
import { Card, CardContent } from "../components/ui/card.tsx";
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCategories } from '../rtk/slices/categories-slice.js';
import { fetchProducts } from '../rtk/slices/products-slice.js';

// مكون Pagination بسيط يتناسب مع نمط shadcn/ui
const Pagination = ({ currentPage, totalPages, onPageChange, isRTL }) => {
  const pageNumbers = [];
  const maxPageButtons = 5; // أقصى عدد لأزرار الأرقام الظاهرة

  let startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
  let endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

  if (totalPages > maxPageButtons) {
    if (endPage === totalPages) {
      startPage = Math.max(1, totalPages - maxPageButtons + 1);
    } else if (startPage === 1) {
      endPage = maxPageButtons;
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div className="flex justify-center items-center space-x-2 mt-8 md:mt-12">
      <Button
        variant="outline"
        size="icon"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
        className="h-9 w-9 p-0"
      >
        <PrevIcon className="h-4 w-4" />
      </Button>

      {startPage > 1 && (
        <>
          <Button variant="outline" size="icon" onClick={() => onPageChange(1)} className="h-9 w-9 p-0">1</Button>
          {startPage > 2 && <span className="px-2">...</span>}
        </>
      )}

      {pageNumbers.map(number => (
        <Button
          key={number}
          variant={currentPage === number ? "default" : "outline"}
          size="icon"
          onClick={() => onPageChange(number)}
          className="h-9 w-9 p-0"
        >
          {number}
        </Button>
      ))}

      {endPage < totalPages && (
        <>
          {endPage < totalPages && <span className="px-2">...</span>}
          <Button variant="outline" size="icon" onClick={() => onPageChange(totalPages)} className="h-9 w-9 p-0">{totalPages}</Button>
        </>
      )}

      <Button
        variant="outline"
        size="icon"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next page"
        className="h-9 w-9 p-0"
      >
        <NextIcon className="h-4 w-4" />
      </Button>
    </div>
  );
};



const Products = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();


  // --- جلب البيانات من Redux ---
  const { categories, loading: loadingCategories, error: categoriesError } = useSelector((state) => state.categories);
  const { products, loading: loadingProducts, error: productsError } = useSelector((state) => state.products);

  // --- useEffect لجلب البيانات عند تحميل المكون ---
  useEffect(() => {
    // جلب الفئات والمنتجات عند تحميل المكون لأول مرة
    dispatch(fetchCategories());
    dispatch(fetchProducts());
  }, [dispatch]);

  // --- حالات الفلاتر والترقيم ---
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [priceRange, setPriceRange] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = viewMode === 'grid' ? 12 : 5;

  // useEffect للتمرير لأعلى عند تغيير الصفحة
  useEffect(() => {
    const productsSection = document.getElementById('products-section');
    if (productsSection) {
      productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [currentPage]);

  const priceRanges = [
    { key: 'all', label: t('filter.allPrices') },
    { key: 'under1000', label: t('filter.under1000') },
    { key: '1000-2000', label: t('filter.range1000') },
    { key: '2000-3000', label: t('filter.range2000') },
    { key: 'over3000', label: t('filter.over3000') }
  ];


  // التأكد من تطبيق الاتجاه الصحيح
  useEffect(() => {
    const isRTL = i18n.language === 'ar';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;

    window.scrollTo(0, 0);
  }, [i18n.language]);

  // --- حساب المنتجات المصفاة ---
  const filteredProducts = useMemo(() => {
    // لا تقم بالتصفية إلا إذا كانت مصفوفة المنتجات موجودة
    if (!products || products.length === 0) return [];

    return products.filter(product => {
      const currentLanguage = i18n.language;
      const title = product[`name_${currentLanguage}`] || product.name_en || '';

      const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || product.category == selectedCategory;

      let matchesPrice = true;
      if (priceRange !== 'all') {
        const price = parseFloat(product.price);
        if (!isNaN(price)) {
          switch (priceRange) {
            case 'under1000': matchesPrice = price < 1000; break;
            case '1000-2000': matchesPrice = price >= 1000 && price < 2000; break;
            case '2000-3000': matchesPrice = price >= 2000 && price < 3000; break;
            case 'over3000': matchesPrice = price >= 3000; break;
            default: break;
          }
        }
      }
      return matchesSearch && matchesCategory && matchesPrice;
    });
  }, [searchTerm, selectedCategory, priceRange, i18n.language, products]); // أضفنا `products` إلى الاعتماديات

  // إعادة تعيين الصفحة إلى 1 عند تغيير الفلاتر
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedCategory, priceRange, viewMode]);

  // --- حساب المنتجات للصفحة الحالية ---
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentItems = filteredProducts.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredProducts.length / ITEMS_PER_PAGE);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };



  return (
    <div className="relative min-h-screen bg-gray-50 z-10">
      <div className="pt-20 pb-12">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="flex items-center gap-4 mb-8">
            <Button onClick={() => navigate('/')} variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" />
              {t('nav.home')}
            </Button>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-800">{t('nav.products')}</h1>
              <p className="text-gray-600 mt-1">{t('products.subtitle')}</p>
            </div>
          </div>

          {/* Filters */}
          <Card className="mb-8 shadow-sm">
            <CardContent className="p-4 md:p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                {/* Search */}
                <div className="relative">
                  <Search className={`absolute top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 ${i18n.language === 'ar' ? 'right-3' : 'left-3'}`} />
                  <Input
                    placeholder={t('filter.search')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className={`${i18n.language === 'ar' ? 'pr-10' : 'pl-10'}`}
                  />
                </div>

                {/* Category Filter (من الـ API) */}
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="h-10 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  disabled={loadingCategories}
                >
                  <option value="all">{t('filter.all')}</option>
                  {loadingCategories ? (
                    <option disabled>Loading categories...</option>
                  ) : (
                    categories.map(category => (
                      <option key={category.id} value={category.id}>
                        {category[`category_${i18n.language}`] || category.category_en}
                      </option>
                    ))
                  )}
                </select>

                {/* Price Filter */}
                <select
                  value={priceRange}
                  onChange={(e) => setPriceRange(e.target.value)}
                  className="h-10 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  {priceRanges.map(range => (
                    <option key={range.key} value={range.key}>
                      {range.label}
                    </option>
                  ))}
                </select>

                {/* View Mode */}
                <div className="flex gap-2">
                  <Button variant={viewMode === 'grid' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('grid')} aria-label="Grid view" className="h-10 w-10">
                    <Grid className="h-5 w-5" />
                  </Button>
                  <Button variant={viewMode === 'list' ? 'default' : 'outline'} size="icon" onClick={() => setViewMode('list')} aria-label="List view" className="h-10 w-10">
                    <List className="h-5 w-5" />
                  </Button>
                </div>
              </div>
              <div className="text-sm text-gray-600">
                {t('filter.showing')} {currentItems.length} {t('filter.of', { defaultValue: 'of' })} {filteredProducts.length} {t('filter.products')}
              </div>
            </CardContent>
          </Card>

          {/* عرض المحتوى بناءً على حالة التحميل والخطأ */}
          <div id="products-section">
            {loadingProducts ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-amber-600"></div>
              </div>
            ) : productsError ? (
              <div className="text-center py-12 text-red-500">
                <h3 className="text-xl font-semibold">{t('filter.error', 'An error occurred')}</h3>
                <p>{productsError}</p>
              </div>
            ) : currentItems.length > 0 ? (
              <>
                <div className={`grid gap-6 ${viewMode === 'grid' ? 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4' : 'grid-cols-1'}`}>
                  {currentItems.map((product) => {
                    const currentLanguage = i18n.language;
                    const title = product[`name_${currentLanguage}`] || product.name_en;
                    const description = product[`description_${currentLanguage}`] || product.description_en;
                    const imageUrl = `${process.env.REACT_APP_PROJECTS_API || ''}assest/imgs/${product.img}`;

                    const categoryObject = categories.find(cat => cat.id == product.category);
                    const categoryName = categoryObject ? (categoryObject[`category_${currentLanguage}`] || categoryObject.category_en) : '';

                    return (
                      <motion.div
                        key={product.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        layout
                      >
                        <Link to={`/product/${product.id}`}>
                          <Card className="group hover:shadow-lg transition-shadow duration-300 cursor-pointer overflow-hidden h-full flex flex-col">
                            <div className={`${viewMode === 'grid' ? 'block' : 'sm:flex'}`}>
                              <div className={`relative overflow-hidden ${viewMode === 'grid' ? 'h-52 sm:h-60' : 'w-full sm:w-48 md:w-56 h-48 sm:h-auto flex-shrink-0'}`}>
                                <img
                                  src={imageUrl}
                                  alt={title}
                                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                />
                                <div className="absolute top-2 right-2">
                                  <span className="bg-white/90 px-2 py-1 rounded-full text-xs font-semibold text-amber-700 shadow">
                                    ${product.price}
                                  </span>
                                </div>
                              </div>
                              <CardContent className={`p-4 flex flex-col flex-grow ${viewMode === 'list' ? 'sm:p-5' : ''}`}>
                                {categoryName && (
                                  <div className="mb-2">
                                    <span className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                                      {categoryName}
                                    </span>
                                  </div>
                                )}
                                <h3 className="text-lg font-semibold text-gray-800 mb-1.5 group-hover:text-amber-600 transition-colors">
                                  {title}
                                </h3>
                                <p className={`text-gray-600 text-sm flex-grow ${viewMode === 'grid' ? 'line-clamp-3' : 'line-clamp-2 mb-3'}`}>
                                  {description}
                                </p>
                                <div className="mt-auto pt-3">
                                  <Button size="sm" className="w-full sm:w-auto bg-amber-500 hover:bg-amber-600 text-white">
                                    {t('product.viewDetails')}
                                  </Button>
                                </div>
                              </CardContent>
                            </div>
                          </Card>
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>

                {totalPages > 1 && (
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    isRTL={i18n.language === 'ar'}
                  />
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <Filter className="w-16 h-16 mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  {t('filter.noProducts')}
                </h3>
                <p className="text-gray-500">
                  {t('filter.tryDifferent')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Products;